import Game from './game';
import BaseObject, { BaseObjectParams } from './baseObject';
import LevelScene from './scene/LevelScene';
import Ship, { ShipParams } from './element/ship/ship';
import AttackMoveToOrder from './order/attackMoveToOrder';

import drawLifeBar from '../utils/lifeBar';
import { Faction } from '../utils/faction';
import { Iterator } from '../utils/types';
import { CoordsType, getAngleBy2XY, getXYByDistanceAndAngle } from '../utils/geometry';

import * as shipConfig from '../config/ships.config';


export interface AlertGaugeParams extends BaseObjectParams {
  scene?: LevelScene;
  context?: CanvasRenderingContext2D;
  progression?: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}


export default class AlertGauge extends BaseObject implements AlertGaugeParams {
  context!: CanvasRenderingContext2D;
  progression = 0;
  width!: number;
  height!: number;
  x: number = 0;
  y: number = 0;

  maxProgression = 100;
  reinforcements: boolean[] = [false, false, false, false];

  get scene(): LevelScene {
    return Game.instance.scene as LevelScene;
  }

  constructor(params: AlertGaugeParams) {
    super();
    this.applyParams(params);

    if (!this.width)
      this.width = Game.instance.canvasWidth / 4;
    if (!this.height)
      this.height = Game.instance.canvasHeight / 15;

    if (!this.x)
      this.x = Game.instance.canvasWidth / 2 - this.width / 2; // centered
    if (!this.y)
      this.y = this.height; // at the top
  }

  update(modifier: number): void {
    if (Game.instance?.paused)
      return;
    this.addProgress(modifier * 0.1); // 1% every 10 seconds
  }

  draw(): CanvasRenderingContext2D {
    /* A bar with 4 thresholds (1/4, middle, 3/4, and fully filled) */
    const { context: ctx, x, y, width, height } = this;

    ctx.save();

    drawLifeBar({
      context: ctx,
      x,
      y,
      width,
      height,
      startColor: { R: 255, G: 0, B: 0, A: 0.5 },
      endColor: { R: 0, G: 255, B: 0, A: 0.5 },
      lineColor: { R: 255, G: 255, B: 255, A: 1 },
      ratio: this.progression / this.maxProgression,
    });

    ctx.strokeStyle = 'grey';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + width / 4, y);
    ctx.lineTo(x + width / 4, y + height);
    ctx.closePath();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x + width / 2, y);
    ctx.lineTo(x + width / 2, y + height);
    ctx.closePath();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x + width * 3 / 4, y);
    ctx.lineTo(x + width * 3 / 4, y + height);
    ctx.closePath();
    ctx.stroke();

    const text = 'Enemy awareness';
    ctx.fillStyle = 'white';
    ctx.font = '30px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    ctx.fillText(text, x + width / 2, y - height / 2);

    ctx.restore();
    return ctx;
  }

  addProgress(amount: number): void {
    this.progression = Math.min(this.progression + amount, this.maxProgression);
    if (!this.reinforcements[0] && this.progression >= this.maxProgression * 0.25)
      this.addReinforcements(0);
    if (!this.reinforcements[1] && this.progression >= this.maxProgression / 2)
      this.addReinforcements(1);
    if (!this.reinforcements[2] && this.progression >= this.maxProgression * 0.75)
      this.addReinforcements(2);
    if (!this.reinforcements[3] && this.progression === this.maxProgression)
      this.addReinforcements(3);
  }

  addReinforcements(waveNumber: number): void {
    this.reinforcements[waveNumber] = true;

    switch (waveNumber) {
      case 0:
        this.spawnWave([{ ...shipConfig.basicJet }]);
        break;

      case 1:
        this.spawnWave([
          { ...shipConfig.basicJet },
          { ...shipConfig.basicJet },
        ]);
        break;

      case 2:
        this.spawnWave([
          { ...shipConfig.basicJet },
          { ...shipConfig.basicJet },
          { ...shipConfig.basicJet },
        ]);
        break;

      case 3:
        this.spawnWave([
          { ...shipConfig.destroyer } as ShipParams,
          { ...shipConfig.destroyer } as ShipParams,
          { ...shipConfig.destroyer } as ShipParams,
          { ...shipConfig.destroyer } as ShipParams,
        ]);
        break;
    }
  }

  spawnWave(configArray: Partial<ShipParams>[]): void {
    const additionalShipParams = { scene: this.scene, context: Game.instance.contexts.mainCanvas };

    const enemiesArray: Ship[] = [];
    configArray.forEach((enemyConfig) => {
      const newEnemy = new Ship({
        ...enemyConfig,
        ...additionalShipParams,
        id: BaseObject.getNewIdentifier(), // manually set an id,
        faction: Faction.ENEMY,
      } as ShipParams);
      enemiesArray.push(newEnemy);
    });

    /* Random point near the CapitalShip */
    const { x, y } = getXYByDistanceAndAngle({
      x: Game.instance.player.capitalShip.x,
      y: Game.instance.player.capitalShip.y,
      angle: Math.random() * 360,
      distance: 1000,
    });

    const spawnCoords: Iterator<CoordsType> = Game.instance.controlSchemes.rts.getCoordsForMultipleElements({ x, y }, enemiesArray);
    enemiesArray.forEach((enemy) => {
      enemy.x = spawnCoords[enemy.id].x;
      enemy.y = spawnCoords[enemy.id].y;
      enemy.scene = Game.instance.scene as LevelScene;
      enemy.lookAngle = getAngleBy2XY({
        x1: enemy.x,
        y1: enemy.y,
        x2: Game.instance.player.capitalShip.x,
        y2: Game.instance.player.capitalShip.y,
      });
      enemy.behaviour.setOrder(new AttackMoveToOrder({
        target: Game.instance.player.capitalShip,
      }));
      enemy.jumpIn();
    });
  }
}
